import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
    format,
    subDays,
    addWeeks,
    addDays,
    isBefore,
    parseISO,
} from 'date-fns';

import { isEmpty } from 'ramda';

import { decimal } from '../utils/formatters';
import { tooltipLabelCallback } from '../utils/charts/tooltip-label';

import PointChart from '../components/charts/PointChart';
import ChartLegend from './ChartLegend';
import { dateFormatOptions } from '../constants/dateFormat';
import usePacingGraph from '../components/hooks/usePacingGraph';
import { getPacingGraphOverrides } from './pacingGraphOverrides';

const legendElements = (
    currentColor: string,
    goalCompleteLabel: string,
): Array<{
    color: string;
    label: string;
    icon?: string;
}> => {
    return [
        {
            color: '#FFCC00',
            label: 'On track Pace',
            icon: 'solid-line',
        },
        {
            color: currentColor,
            label: 'Current Pace',
            icon: 'dashed-line',
        },
        {
            color: currentColor,
            label: 'Current Value',
        },
        {
            color: '#666699',
            label: `${goalCompleteLabel} Completion`,
        },
        {
            color: '#0099CC',
            label: 'Forecasted Final Value',
        },
    ];
};

const PacingGraph = () => {
    const { id: goalId } = useParams();
    const params = new URLSearchParams(location.search.slice(1));
    const clientId = params.get('clientId');
    const token = params.get('token');
    const userId = params.get('userId');
    const locale = params.get('locale') || 'en-US';
    const pacingGraphOverrides = getPacingGraphOverrides(params);

    const { data: goal, isLoading } = usePacingGraph({
        goalId,
        clientId,
        token,
        userId,
    });

    const dateFormat = dateFormatOptions.find(
        option => option.value === locale,
    )?.name;

    const shortDate = (date: Date) => format(date, dateFormat);

    const ref = useRef(null);
    const refComplete = useRef(null);

    const reportGoal = goal ? { ...goal, ...pacingGraphOverrides } : goal;
    const hasCurrentGoal = reportGoal && !isEmpty(reportGoal);
    if (!hasCurrentGoal || isLoading) return null;

    const {
        startDate,
        endDate,
        targetValue,
        currentValue,
        metricUpdatedAt,
        description,
        statusId,
    } = reportGoal;

    const formattedStartDate = shortDate(new Date(startDate));
    const lowerDateLimit = shortDate(subDays(new Date(startDate), 1));
    const formattedEndDate = shortDate(new Date(endDate));
    const formattedMetricUpdated = shortDate(new Date(metricUpdatedAt));

    const potentialRemainingDays =
        (new Date(endDate).getTime() - new Date().getTime()) /
            (3600 * 1000 * 24) +
        1;

    const remainingDays =
        potentialRemainingDays > 0 ? potentialRemainingDays : 0;

    const totalDays =
        (new Date(endDate).getTime() - new Date(startDate).getTime()) /
            (3600 * 1000 * 24) +
        1;

    const elapsedDays =
        totalDays - remainingDays !== 0 ? totalDays - remainingDays : 1;

    const forecastedRemainingDays = Math.round(
        (targetValue - currentValue) / (currentValue / elapsedDays),
    );
    const forecastedCompleteDate = metricUpdatedAt
        ? shortDate(addDays(parseISO(metricUpdatedAt), forecastedRemainingDays))
        : null;
    const forecastedFinalValue =
        Math.round(currentValue / elapsedDays) * totalDays;

    const isForecastedCloser =
        forecastedCompleteDate && endDate
            ? isBefore(parseISO(forecastedCompleteDate), parseISO(endDate))
            : false;

    const upperDateLimit = isForecastedCloser
        ? shortDate(addWeeks(parseISO(endDate), 1))
        : shortDate(addWeeks(parseISO(forecastedCompleteDate), 1));

    const currentValuePoint = { x: formattedMetricUpdated, y: currentValue };
    const forecastedCompleteDatePoint = {
        x: forecastedCompleteDate,
        y: targetValue,
    };
    const forecastedFinalValuePoint = {
        x: formattedEndDate,
        y: forecastedFinalValue,
    };

    const currentPaceColor = isForecastedCloser ? '#3fae2a' : '#e5512e';
    const currentGoalCompleteLabel =
        statusId === 5 ? 'Goal' : 'Forecasted Goal';

    const datasets = [
        {
            id: 0,
            color: [currentPaceColor, '#666699', '#0099CC'],
            pointStyle: ['circle', 'triangle', 'rectRot'],
            data: [
                currentValuePoint,
                forecastedCompleteDatePoint,
                forecastedFinalValuePoint,
            ],
        },
    ];

    return (
        <div className="gds-layout__container -m-t-4 -m-t-3-sm">
            <div
                className="pacing-graph-container"
                style={{
                    backgroundColor: 'white',
                }}
                ref={refComplete}
            >
                <div
                    className="pacing-graph gds-flex__item gds-flex gds-flex--justify-center gds-flex--content-center gds-flex--align-center -p-h-4 -p-v-2"
                    data-test="pacing-graph"
                >
                    <PointChart
                        ref={ref}
                        data={{
                            datasets,
                            labels: [],
                        }}
                        options={{
                            xType: 'date',
                            yType: 'decimal',
                            config: {
                                showLine: false,
                                responsive: true,
                                locale,
                                scales: {
                                    x: {
                                        type: 'time',
                                        display: true,
                                        time: {
                                            unit: 'day',
                                        },
                                        min: lowerDateLimit,
                                        max: upperDateLimit,
                                    },
                                    y: {
                                        type: 'linear',
                                        min: 0,
                                        max:
                                            Math.max(
                                                targetValue,
                                                currentValue,
                                                forecastedFinalValue,
                                            ) * 1.2,
                                    },
                                },
                                plugins: {
                                    tooltip: {
                                        callbacks: {
                                            label: context =>
                                                tooltipLabelCallback(
                                                    context,
                                                    decimal,
                                                    description.label,
                                                    false,
                                                    false,
                                                    true,
                                                    targetValue,
                                                ),
                                        },
                                    },
                                    datalabels: {
                                        display: false,
                                    },
                                    legend: {
                                        display: false,
                                    },
                                    annotation: {
                                        annotations: [
                                            {
                                                type: 'line',
                                                xMin: formattedStartDate,
                                                xMax: isForecastedCloser
                                                    ? forecastedFinalValuePoint.x
                                                    : forecastedCompleteDatePoint.x,
                                                yMin: 0,
                                                yMax: isForecastedCloser
                                                    ? forecastedFinalValuePoint.y
                                                    : forecastedCompleteDatePoint.y,
                                                borderColor: currentPaceColor,
                                                borderDash: [10, 10],
                                                drawTime: 'beforeDraw',
                                            },
                                            {
                                                type: 'line',
                                                xMin: formattedStartDate,
                                                xMax: formattedEndDate,
                                                yMin: 0,
                                                yMax: targetValue,
                                                borderColor: 'orange',
                                            },
                                            {
                                                type: 'line',
                                                xMin: formattedEndDate,
                                                xMax: formattedEndDate,
                                                borderColor: 'gray',
                                                borderDash: [10, 10],
                                                drawTime: 'beforeDraw',
                                                label: {
                                                    rotation: 'auto',
                                                    position: 'end',
                                                    content: 'Goal End Date',
                                                    enabled: true,
                                                },
                                            },
                                            {
                                                type: 'line',
                                                yMin: targetValue,
                                                yMax: targetValue,
                                                borderColor: 'gray',
                                                borderDash: [10, 10],
                                                drawTime: 'beforeDraw',
                                                label: {
                                                    content: 'Target Value',
                                                    enabled: true,
                                                    position: 'start',
                                                },
                                            },
                                        ],
                                    },
                                },
                            },
                        }}
                        isLoading={isLoading}
                    />
                </div>
                <div className="gds-flex__item gds-flex gds-flex--justify-center gds-flex--content-center gds-flex--align-center -p-h-4 -p-v-2">
                    <ChartLegend
                        legendElements={legendElements(
                            currentPaceColor,
                            currentGoalCompleteLabel,
                        )}
                    />
                </div>
            </div>
        </div>
    );
};

export default PacingGraph;
